import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const P = {
  dark:     '#2D4F4A',
  teal:     '#2D4F4A',
  tealMid:  '#3D6560',
  gold:     '#C4956A',
  goldDark: '#A07848',
  goldPale: 'rgba(196,149,106,0.10)',
  goldLine: 'rgba(196,149,106,0.25)',
  rose:     '#F5B8A0',
  cream:    '#FAF7F4',
  white:    '#FFFFFF',
  gray:     '#9CA3AF',
  grayMid:  '#6B7280',
  border:   '#E8D5C4',
  error:    '#EF4444',
};

const font     = "'Georgia','Times New Roman',serif";
const fontSans = "'MyriadPro','Helvetica Neue',Arial,sans-serif";

const PAISES = [
  {cod:'+57',flag:'🇨🇴',name:'Colombia'},
  {cod:'+1', flag:'🇺🇸',name:'EE.UU.'},
  {cod:'+52',flag:'🇲🇽',name:'México'},
  {cod:'+34',flag:'🇪🇸',name:'España'},
  {cod:'+54',flag:'🇦🇷',name:'Argentina'},
  {cod:'+56',flag:'🇨🇱',name:'Chile'},
  {cod:'+51',flag:'🇵🇪',name:'Perú'},
  {cod:'+58',flag:'🇻🇪',name:'Venezuela'},
  {cod:'+593',flag:'🇪🇨',name:'Ecuador'},
  {cod:'+44',flag:'🇬🇧',name:'Reino Unido'},
];

const PATOLOGIAS = [
  'Estrés','Ansiedad','Depresión','Autoestima',
  'Pérdida de un ser querido','Divorcio o terminación de una relación',
  'Autolesiones','Ataques de pánico','Trastornos del sueño',
  'Trastornos alimenticios','Déficit de atención TDAH',
  'Adicciones de cualquier tipo','Egos','Autocontrol','Ira',
  'Proyecto de vida','Problemas familiares','Problemas económicos',
  'Crisis de identidad','Apego','Insensibilidad o apatía',
  'Pensamientos suicidas','Fobias',
];

const GENEROS         = ['Femenino','Masculino','No binario','Prefiero no decir'];
const ESTADOS_CIVILES = ['Soltero/a','Casado/a','Unión libre','Divorciado/a','Viudo/a','Separado/a'];
const EDUCACION_OPTS  = ['Primaria','Secundaria','Técnico / Tecnológico','Universitario','Posgrado','Ninguno'];

const ETAPAS = [
  { num:'01', titulo:'Datos personales',        sub:'Cuéntanos quién eres' },
  { num:'02', titulo:'Información de contacto', sub:'¿Cómo podemos comunicarnos?' },
  { num:'03', titulo:'Lugar de residencia',      sub:'¿Dónde estás ubicado?' },
  { num:'04', titulo:'Formación y ocupación',    sub:'Tu perfil profesional' },
  { num:'05', titulo:'Motivo de consulta',       sub:'¿En qué podemos acompañarte?' },
];

const INIT = {
  nombre:'', apellido:'', cedula:'', fecha_nacimiento:'', genero:'', estado_civil:'',
  indicativo:'+57', telefono:'', email:'', contacto_emergencia:'', telefono_emergencia:'',
  pais:'Colombia', departamento:'', ciudad:'',
  educacion:'', ocupacion:'',
  patologias:[], patologias_otro:'',
};

// ── Componentes base ──────────────────────────────────────────

function Campo({ label, required, children, error }) {
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <label style={{
        display: 'block', fontFamily: fontSans,
        fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: P.gold, marginBottom: '0.25rem',
      }}>
        {label}{required && <span style={{ color: P.rose, marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {error && (
        <p style={{ color: P.error, fontSize: '0.75rem', marginTop: '0.35rem', fontFamily: fontSans }}>
          ⚠ {error}
        </p>
      )}
    </div>
  );
}

const lineInp = {
  width: '100%', background: 'transparent',
  border: 'none', borderBottom: `1.5px solid ${P.border}`,
  borderRadius: 0, padding: '0.4rem 0',
  fontSize: '0.95rem', color: P.teal, outline: 'none',
  fontFamily: fontSans, boxSizing: 'border-box',
  transition: 'border-color 0.25s',
};

const selInp = {
  ...lineInp, cursor: 'pointer', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23C4956A' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center',
  paddingRight: '1.5rem',
};

const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 2rem' };

// ── Pantalla de éxito ─────────────────────────────────────────
function Exito() {
  return (
    <div style={{
      minHeight: '100vh', background: P.cream, fontFamily: fontSans,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '2rem', textAlign: 'center',
    }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes scaleIn { from { opacity:0; transform:scale(0.7); } to { opacity:1; transform:scale(1); } }
      `}</style>
      <img src="/images/logo-horizontal.png" alt="Dani Lopera"
        style={{ height: 110, objectFit: 'contain', marginBottom: '2.5rem', animation: 'fadeUp 0.7s ease both' }} />
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        border: `2px solid ${P.gold}`, display: 'flex', alignItems: 'center',
        justifyContent: 'center', margin: '0 auto 1.8rem',
        fontSize: '1.6rem', color: P.gold,
        animation: 'scaleIn 0.6s 0.3s cubic-bezier(0.175,0.885,0.32,1.275) both',
      }}>✓</div>
      <h2 style={{
        fontFamily: font, fontWeight: 400, color: P.teal,
        fontSize: '1.8rem', marginBottom: '1rem', letterSpacing: '0.04em',
        animation: 'fadeUp 0.7s 0.5s ease both',
      }}>Información recibida</h2>
      <p style={{
        color: P.grayMid, lineHeight: 1.9, maxWidth: 400,
        marginBottom: '2rem', fontSize: '0.95rem',
        animation: 'fadeUp 0.7s 0.7s ease both',
      }}>
        Gracias por tu confianza.<br />
        La psicóloga Dani Lopera revisará tu información y se pondrá en contacto contigo muy pronto.
      </p>
      <div style={{ height: 1, width: 60, background: P.gold, margin: '0 auto 1.5rem', animation: 'fadeUp 0.7s 0.9s ease both' }} />
      <p style={{ color: P.gray, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', animation: 'fadeUp 0.7s 1s ease both' }}>
        Información estrictamente confidencial
      </p>
    </div>
  );
}

// ── Formulario principal ──────────────────────────────────────
export default function FormularioPublico() {
  const [form, setForm]         = useState(INIT);
  const [errors, setErrors]     = useState({});
  const [etapa, setEtapa]       = useState(0);
  const [dir, setDir]           = useState(1);       // 1=adelante, -1=atrás
  const [animando, setAnimando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado]   = useState(false);
  const [serverError, setServerError] = useState('');
  const contenedorRef = useRef(null);
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(200);

  useEffect(() => {
    if (!headerRef.current) return;
    const obs = new ResizeObserver(() => setHeaderH(headerRef.current.offsetHeight));
    obs.observe(headerRef.current);
    setHeaderH(headerRef.current.offsetHeight);
    return () => obs.disconnect();
  }, []);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const togglePat = p => setForm(f => ({
    ...f,
    patologias: f.patologias.includes(p)
      ? f.patologias.filter(x => x !== p)
      : [...f.patologias, p],
  }));

  // Inyectar CSS de animaciones una sola vez
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInRight  { from { opacity:0; transform:translateX(60px);  } to { opacity:1; transform:translateX(0); } }
      @keyframes slideInLeft   { from { opacity:0; transform:translateX(-60px); } to { opacity:1; transform:translateX(0); } }
      @keyframes slideOutRight { from { opacity:1; transform:translateX(0); } to { opacity:0; transform:translateX(60px);  } }
      @keyframes slideOutLeft  { from { opacity:1; transform:translateX(0); } to { opacity:0; transform:translateX(-60px); } }
      @keyframes progressGrow  { from { width:0; } to { width:100%; } }
      .campo-inp:focus { border-color: #C4956A !important; }
      ::placeholder { color: #B0B8C1; }
      select option { background: #FAF7F4; color: #2D4F4A; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const validarEtapa = () => {
    const e = {};
    if (etapa === 0) {
      if (!form.nombre.trim())   e.nombre   = 'Campo requerido';
      if (!form.apellido.trim()) e.apellido = 'Campo requerido';
    }
    if (etapa === 1) {
      if (!form.telefono.trim()) e.telefono = 'Campo requerido';
      if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email inválido';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const irA = (nueva) => {
    if (animando) return;
    const nuevaDir = nueva > etapa ? 1 : -1;
    setDir(nuevaDir);
    setAnimando(true);
    setTimeout(() => {
      setEtapa(nueva);
      setAnimando(false);
      contenedorRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 280);
  };

  const siguiente = () => {
    if (!validarEtapa()) return;
    if (etapa < ETAPAS.length - 1) irA(etapa + 1);
  };

  const anterior = () => {
    if (etapa > 0) irA(etapa - 1);
  };

  const enviar = async () => {
    if (!validarEtapa()) return;
    setEnviando(true);
    setServerError('');
    try {
      const pats = [...form.patologias];
      if (form.patologias_otro.trim()) pats.push('Otros: ' + form.patologias_otro.trim());
      const res  = await fetch(`${API_BASE}/formulario_publico.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, patologias: pats }),
      });
      const data = await res.json();
      if (res.ok) setEnviado(true);
      else setServerError(data.error || 'Error al enviar. Intenta de nuevo.');
    } catch {
      setServerError('Error de conexión. Verifica tu internet.');
    } finally {
      setEnviando(false);
    }
  };

  if (enviado) return <Exito />;

  const pct = Math.round(((etapa + 1) / ETAPAS.length) * 100);
  const etapaData = ETAPAS[etapa];

  // Animación del contenido
  const animStyle = animando ? {
    animation: `${dir > 0 ? 'slideOutLeft' : 'slideOutRight'} 0.28s ease forwards`,
  } : {
    animation: `${dir > 0 ? 'slideInRight' : 'slideInLeft'} 0.35s cubic-bezier(0.25,0.46,0.45,0.94) both`,
  };

  return (
    <div style={{ minHeight: '100vh', background: P.cream, fontFamily: fontSans, display: 'flex', flexDirection: 'column' }}>

      {/* ── Header fijo ── */}
      <div ref={headerRef} style={{
        background: P.white, borderBottom: `1px solid ${P.border}`,
        padding: '0.6rem 1.5rem 0', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        boxShadow: '0 2px 20px rgba(45,79,74,0.06)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          <img src="/images/logo-horizontal.png" alt="Dani Lopera Psicóloga"
            style={{ height: 100, objectFit: 'contain' }} />
        </div>

        {/* Etapas + barra compactas */}
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          {/* Indicadores */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', marginBottom: '0.35rem' }}>
            {ETAPAS.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  border: `1.5px solid ${i <= etapa ? P.gold : P.border}`,
                  background: i < etapa ? P.gold : i === etapa ? P.goldPale : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.55rem', fontWeight: 700,
                  color: i < etapa ? P.white : i === etapa ? P.gold : P.gray,
                  transition: 'all 0.4s ease', flexShrink: 0,
                }}>
                  {i < etapa ? '✓' : e.num}
                </div>
                {i < ETAPAS.length - 1 && (
                  <div style={{ width: 24, height: 1, background: i < etapa ? P.gold : P.border, transition: 'background 0.4s' }} />
                )}
              </div>
            ))}
          </div>

          {/* Barra de progreso */}
          <div style={{ height: 2.5, background: P.border, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: `linear-gradient(90deg, ${P.gold}, ${P.goldDark})`,
              width: `${pct}%`, transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0 0.45rem', fontSize: '0.68rem', color: P.gray }}>
            <span style={{ fontWeight: 600, color: P.gold }}>{etapaData.titulo}</span>
            <span>{pct}% completado</span>
          </div>
        </div>
      </div>

      {/* ── Contenido (paddingTop = altura aprox del header fijo) ── */}
      <div ref={contenedorRef} style={{ flex: 1, overflowY: 'auto', paddingTop: headerH }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '1rem 1.25rem 1rem' }}>

          {/* Título de etapa */}
          <div style={{ ...animStyle, marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.62rem', color: P.gold, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', margin: '0 0 0.2rem' }}>
              Paso {etapaData.num} de 05
            </p>
            <h2 style={{ fontFamily: font, fontWeight: 400, color: P.teal, fontSize: 'clamp(1.2rem,4vw,1.5rem)', margin: '0 0 0.15rem', letterSpacing: '0.03em' }}>
              {etapaData.titulo}
            </h2>
            <p style={{ color: P.grayMid, fontSize: '0.8rem', margin: 0 }}>{etapaData.sub}</p>
            <div style={{ width: 28, height: 2, background: P.gold, marginTop: '0.6rem', borderRadius: 2 }} />
          </div>

          {/* Campos según etapa */}
          <div style={animStyle}>

            {/* ── Etapa 1: Datos personales ── */}
            {etapa === 0 && (
              <div>
                <div style={grid2}>
                  <Campo label="Nombres" required error={errors.nombre}>
                    <input className="campo-inp" style={{ ...lineInp, borderColor: errors.nombre ? P.error : P.border }}
                      value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Tus nombres" />
                  </Campo>
                  <Campo label="Apellidos" required error={errors.apellido}>
                    <input className="campo-inp" style={{ ...lineInp, borderColor: errors.apellido ? P.error : P.border }}
                      value={form.apellido} onChange={e => set('apellido', e.target.value)} placeholder="Tus apellidos" />
                  </Campo>
                  <Campo label="Cédula / Documento de identidad">
                    <input className="campo-inp" style={lineInp}
                      value={form.cedula} onChange={e => set('cedula', e.target.value)} placeholder="Número de documento" />
                  </Campo>
                  <Campo label="Fecha de nacimiento">
                    <input className="campo-inp" style={lineInp} type="date"
                      value={form.fecha_nacimiento} onChange={e => set('fecha_nacimiento', e.target.value)} />
                  </Campo>
                  <Campo label="Género">
                    <select className="campo-inp" style={selInp} value={form.genero} onChange={e => set('genero', e.target.value)}>
                      <option value="">Seleccionar</option>
                      {GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Estado sentimental">
                    <select className="campo-inp" style={selInp} value={form.estado_civil} onChange={e => set('estado_civil', e.target.value)}>
                      <option value="">Seleccionar</option>
                      {ESTADOS_CIVILES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Campo>
                </div>
              </div>
            )}

            {/* ── Etapa 2: Contacto ── */}
            {etapa === 1 && (
              <div>
                <Campo label="Teléfono móvil" required error={errors.telefono}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                    <select className="campo-inp" value={form.indicativo} onChange={e => set('indicativo', e.target.value)}
                      style={{ ...selInp, width: 'auto', minWidth: 100, flex: '0 0 auto' }}>
                      {PAISES.map(p => <option key={p.cod} value={p.cod}>{p.flag} {p.cod}</option>)}
                    </select>
                    <input className="campo-inp"
                      style={{ ...lineInp, flex: 1, borderColor: errors.telefono ? P.error : P.border }}
                      type="tel" value={form.telefono}
                      onChange={e => set('telefono', e.target.value.replace(/\D/g, ''))}
                      placeholder="Número sin indicativo" />
                  </div>
                </Campo>
                <div style={grid2}>
                  <Campo label="Correo electrónico" error={errors.email}>
                    <input className="campo-inp"
                      style={{ ...lineInp, borderColor: errors.email ? P.error : P.border }}
                      type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="tu@correo.com" />
                  </Campo>
                </div>
                <div style={{ height: 1, background: P.border, margin: '0.3rem 0 0.8rem' }} />
                <p style={{ fontSize: '0.63rem', color: P.gray, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.6rem' }}>
                  Contacto de emergencia
                </p>
                <div style={grid2}>
                  <Campo label="Nombre del contacto">
                    <input className="campo-inp" style={lineInp}
                      value={form.contacto_emergencia} onChange={e => set('contacto_emergencia', e.target.value)}
                      placeholder="Nombre de familiar o amigo" />
                  </Campo>
                  <Campo label="Teléfono del contacto">
                    <input className="campo-inp" style={lineInp}
                      value={form.telefono_emergencia} onChange={e => set('telefono_emergencia', e.target.value)}
                      placeholder="Número de teléfono" />
                  </Campo>
                </div>
              </div>
            )}

            {/* ── Etapa 3: Ubicación ── */}
            {etapa === 2 && (
              <div>
                <div style={grid2}>
                  <Campo label="País donde resides">
                    <input className="campo-inp" style={lineInp}
                      value={form.pais} onChange={e => set('pais', e.target.value)} placeholder="Colombia" />
                  </Campo>
                  <Campo label="Departamento / Estado">
                    <input className="campo-inp" style={lineInp}
                      value={form.departamento} onChange={e => set('departamento', e.target.value)} placeholder="Ej: Antioquia" />
                  </Campo>
                  <Campo label="Ciudad">
                    <input className="campo-inp" style={lineInp}
                      value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Ej: Medellín" />
                  </Campo>
                </div>
              </div>
            )}

            {/* ── Etapa 4: Formación ── */}
            {etapa === 3 && (
              <div>
                <div style={grid2}>
                  <Campo label="Nivel de estudios">
                    <select className="campo-inp" style={selInp} value={form.educacion} onChange={e => set('educacion', e.target.value)}>
                      <option value="">Seleccionar</option>
                      {EDUCACION_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Ocupación actual">
                    <input className="campo-inp" style={lineInp}
                      value={form.ocupacion} onChange={e => set('ocupacion', e.target.value)}
                      placeholder="Ej: Estudiante, independiente..." />
                  </Campo>
                </div>
              </div>
            )}

            {/* ── Etapa 5: Motivo ── */}
            {etapa === 4 && (
              <div>
                <p style={{ color: P.grayMid, fontSize: '0.78rem', marginBottom: '0.65rem', lineHeight: 1.5 }}>
                  Selecciona lo que más se acerque a lo que estás viviendo.
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '0.3rem', marginBottom: '0.8rem',
                }}>
                  {PATOLOGIAS.map(p => {
                    const sel = form.patologias.includes(p);
                    return (
                      <button key={p} type="button" onClick={() => togglePat(p)} style={{
                        padding: '0.35rem 0.4rem', borderRadius: '6px',
                        cursor: 'pointer', fontSize: '0.72rem',
                        fontFamily: fontSans, textAlign: 'center',
                        lineHeight: 1.3,
                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                        border: `1.5px solid ${sel ? P.gold : P.border}`,
                        background: sel ? P.goldPale : P.white,
                        color: sel ? P.goldDark : P.grayMid,
                        fontWeight: sel ? 700 : 400,
                        boxShadow: sel ? `0 2px 8px rgba(196,149,106,0.18)` : 'none',
                      }}>
                        {sel && <span style={{ color: P.gold, marginRight: '0.2rem', fontSize: '0.65rem' }}>✓</span>}
                        {p}
                      </button>
                    );
                  })}
                </div>
                <Campo label="Otros — especifica si aplica">
                  <input className="campo-inp" style={lineInp}
                    value={form.patologias_otro} onChange={e => set('patologias_otro', e.target.value)}
                    placeholder="Describe brevemente..." />
                </Campo>
              </div>
            )}

          </div>{/* fin animStyle campos */}

          {/* Error servidor */}
          {serverError && (
            <div style={{
              border: `1px solid ${P.error}`, borderRadius: '10px',
              padding: '0.85rem 1rem', marginBottom: '1.5rem',
              color: P.error, fontSize: '0.87rem',
              background: 'rgba(239,68,68,0.06)',
            }}>
              ⚠ {serverError}
            </div>
          )}

          {/* Aviso confidencialidad — solo última etapa */}
          {etapa === 4 && (
            <div style={{
              display: 'flex', gap: '0.5rem', alignItems: 'flex-start',
              padding: '0.6rem 0.75rem', borderRadius: '8px',
              background: P.goldPale, border: `1px solid ${P.goldLine}`,
              marginBottom: '0.8rem',
            }}>
              <span style={{ color: P.gold, flexShrink: 0, fontSize: '0.8rem' }}>◈</span>
              <p style={{ color: P.grayMid, fontSize: '0.72rem', lineHeight: 1.5, margin: 0 }}>
                Tu información es completamente confidencial, protegida bajo los principios éticos de la psicología clínica.
              </p>
            </div>
          )}

          {/* ── Navegación ── */}
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.4rem' }}>
            {etapa > 0 && (
              <button type="button" onClick={anterior} style={{
                padding: '0.65rem 1.1rem', borderRadius: '8px',
                border: `1.5px solid ${P.border}`, background: 'transparent',
                color: P.grayMid, fontSize: '0.82rem', fontFamily: fontSans,
                cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}>
                ← Atrás
              </button>
            )}
            <button
              type="button"
              onClick={etapa === ETAPAS.length - 1 ? enviar : siguiente}
              disabled={enviando}
              style={{
                flex: 1, padding: '0.75rem',
                background: enviando ? P.gray : `linear-gradient(135deg, ${P.gold} 0%, ${P.goldDark} 100%)`,
                color: '#fff', border: 'none', borderRadius: '8px',
                fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', fontFamily: fontSans,
                cursor: enviando ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s', boxShadow: `0 3px 14px rgba(196,149,106,0.3)`,
              }}>
              {enviando ? 'Enviando...' : etapa === ETAPAS.length - 1 ? 'Enviar formulario' : 'Siguiente →'}
            </button>
          </div>

          <p style={{
            textAlign: 'center', color: P.gray,
            fontSize: '0.62rem', marginTop: '1rem',
            letterSpacing: '0.07em', textTransform: 'uppercase',
          }}>
            © Dani Lopera · Psicóloga · Información confidencial
          </p>

        </div>
      </div>
    </div>
  );
}
